<script setup>
import { useForm, router } from '@inertiajs/vue3';
import { getCurrentInstance } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Card from 'primevue/card';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const form = useForm({
    name: '',
});

const submit = () => {
    form.post(route('categories.store'), {
        onStart: () => showLoading('Creating category...'),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Category created successfully',
                life: 3000
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to create category',
                life: 5000
            });
        }
    });
};
</script>

<template>
    <AdminMainLayout>
        <div class="grid gap-6">
            <div class="col-12 lg:col-8">
                <Card>
                    <template #title>
                        <div class="flex align-items-center gap-2">
                            <i class="pi pi-plus text-2xl"></i>
                            <span>{{ $t('Add Category') }}</span>
                        </div>
                    </template>

                <template #content>
                    <form @submit.prevent="submit">
                        <div class="formgrid grid">
                            <div class="field col-12">
                                <label for="name" class="font-semibold">{{ $t('Name') }} <span class="text-red-500">*</span></label>
                                <InputText
                                    id="name"
                                    v-model="form.name"
                                    class="w-full"
                                    :class="{ 'p-invalid': form.errors.name }"
                                    :placeholder="$t('Enter category name')"
                                    autocomplete="name"
                                    :disabled="form.processing" />
                                <small v-if="form.errors.name" class="p-error">{{ form.errors.name }}</small>
                            </div>
                        </div>

                        <div class="flex gap-2 mt-4">
                            <Button
                                type="submit"
                                :label="$t('Save')"
                                icon="pi pi-check"
                                :loading="form.processing"
                                :disabled="form.processing" />

                            <Button
                                :label="$t('Cancel')"
                                icon="pi pi-times"
                                class="p-button-secondary"
                                @click="router.visit(route('categories.index'), {
                                    onStart: () => showLoading('Loading...'),
                                    onFinish: () => hideLoading()
                                })"
                                :disabled="form.processing" />
                        </div>

                        <Message v-if="form.recentlySuccessful" severity="success" :closable="false" class="mt-3">
                            {{ $t('Category saved successfully.') }}
                        </Message>
                    </form>
                    </template>
                </Card>
            </div>

            <div class="col-12 lg:col-4">
                <Card>
                    <template #title>
                        <div class="flex align-items-center gap-2">
                            <i class="pi pi-info-circle"></i>
                            <span>{{ $t('Information') }}</span>
                        </div>
                    </template>
                    <template #content>
                        <p class="m-0">
                            {{ $t('Categories help organize your sites and make them easier to find. Enter a descriptive name for the category that clearly identifies its purpose.') }}
                        </p>
                        <div class="mt-3">
                            <small class="text-gray-600">
                                <strong>{{ $t('Tips:') }}</strong>
                                <ul class="mt-2 pl-4">
                                    <li>{{ $t('Use clear, descriptive names') }}</li>
                                    <li>{{ $t('Keep names concise') }}</li>
                                    <li>{{ $t('Avoid special characters') }}</li>
                                </ul>
                            </small>
                        </div>
                    </template>
                </Card>
            </div>
        </div>
    </AdminMainLayout>
</template>

<style scoped>
.field {
    margin-bottom: 1.5rem;
}

.field label {
    display: block;
    margin-bottom: 0.5rem;
}

.p-error {
    color: var(--red-500);
    font-size: 0.875rem;
    margin-top: 0.25rem;
    display: block;
}
</style>
